import cmykProfile from './cmyk.icc';
import loader from './lcms_rgb_to_cmyk';
import wasm from './lcms_rgb_to_cmyk.wasm';
import srgbProfile from './srgb.icc';

// Rendering intent and flag values from lcms2.h.
const INTENT_RELATIVE_COLORIMETRIC = 1;
const cmsFLAGS_BLACKPOINTCOMPENSATION = 0x2000;

interface LittleCMS {
  _init_transform: (
    rgbProfilePointer: number,
    rgbProfileLength: number,
    cmykProfilePointer: number,
    cmykProfileLength: number,
    rgbToCmykIntent: number,
    rgbToCmykFlags: number,
    cmykToRgbIntent: number,
    cmykToRgbFlags: number,
  ) => void;
  _rgb_to_cmyk: (r: number, g: number, b: number, cmykPointer: number) => void;
  _cmyk_to_rgb: (c: number, m: number, y: number, k: number, rgbPointer: number) => void;
  _malloc: (size: number) => number;
  _free: (pointer: number) => void;
  HEAPU8: Uint8Array;
}

function allocateMemory(buffer: ArrayBuffer, lcms: LittleCMS): number {
  const size = buffer.byteLength;
  const pointer = lcms._malloc(size); // Allocating memory in the WASM module
  new Uint8Array(lcms.HEAPU8.buffer).set(new Uint8Array(buffer), pointer); // Copying data into the WASM memory
  return pointer;
}

let littleCMSModule: LittleCMS | undefined;
// Switching a design to CMYK waits for this, so repeated clicks must share one download, not
// each start a new one. Cleared on failure so the next call retries.
let loading: Promise<void> | undefined;

async function initLittleCMSModule(): Promise<void> {
  const [module, cmykProfileArray, rgbProfileArray] = await Promise.all([
    loader({ locateFile: () => wasm }) as Promise<LittleCMS>,
    fetch(cmykProfile)
      .then((resp) => resp.arrayBuffer())
      .then((buffer) => new Uint8Array(buffer)),
    fetch(srgbProfile)
      .then((resp) => resp.arrayBuffer())
      .then((buffer) => new Uint8Array(buffer)),
  ]);

  const rgbProfilePointer = allocateMemory(rgbProfileArray, module);
  const cmykProfilePointer = allocateMemory(cmykProfileArray, module);
  // Relative colorimetric leaves colours ink can print as they are and moves only the ones it can't
  // reach. Black point compensation maps screen black to the deepest ink black, as Photoshop does.
  // Swatches go without it: they show how the inks print, the print's own black included.
  module._init_transform(
    rgbProfilePointer,
    rgbProfileArray.length,
    cmykProfilePointer,
    cmykProfileArray.length,
    INTENT_RELATIVE_COLORIMETRIC,
    cmsFLAGS_BLACKPOINTCOMPENSATION,
    INTENT_RELATIVE_COLORIMETRIC,
    0,
  );
  littleCMSModule = module;
}

export function loadLittleCMSModule(): Promise<void> {
  loading ??= initLittleCMSModule().catch((error) => {
    loading = undefined;
    throw error;
  });
  return loading;
}

export function getModule(): LittleCMS | undefined {
  return littleCMSModule;
}
